import { stripEmoji } from './stripEmoji'

// Register global listeners that sanitize emoji characters from text inputs and textareas.
// This keeps main.tsx small and allows testing or toggling the feature.
export function registerEmojiSanitizer(): void {
	function sanitizeElement(el: HTMLElement) {
		if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return

		// Only handle text-like inputs
		if (
			el instanceof HTMLInputElement &&
			el.type &&
			!['text', 'search', 'url', 'tel', 'email'].includes(el.type)
		)
			return

		try {
			const start = el.selectionStart
			const before = el.value
			const cleaned = stripEmoji(before)
			if (cleaned !== before) {
				el.value = cleaned
				// restore selection as best-effort
				const newPos = Math.max(0, (start ?? 0) - (before.length - cleaned.length))
				el.setSelectionRange(newPos, newPos)
			}
		} catch {
			// ignore (some inputs may throw on selection operations)
		}
	}

	// input handler (typing)
	document.addEventListener(
		'input',
		(ev) => {
			const target = ev.target as HTMLElement | null
			if (!target) return
			sanitizeElement(target)
		},
		{ capture: true, passive: true }
	)

	// paste handler: sanitize clipboard text before insertion
	document.addEventListener(
		'paste',
		(ev) => {
			const target = ev.target as HTMLElement | null
			if (!target) return
			if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return
			const clipboard = ev.clipboardData?.getData('text')
			if (!clipboard) return
			const cleaned = stripEmoji(clipboard)
			if (cleaned !== clipboard) {
				ev.preventDefault()
				// insert cleaned text at caret position
				const el = target
				const start = el.selectionStart ?? 0
				const end = el.selectionEnd ?? 0
				const newVal = el.value.slice(0, start) + cleaned + el.value.slice(end)
				el.value = newVal
				const pos = start + cleaned.length
				el.setSelectionRange(pos, pos)
			}
		},
		{ capture: true }
	)

	// drop handler: sanitize text drops
	document.addEventListener(
		'drop',
		(ev) => {
			const target = ev.target as HTMLElement | null
			if (!target) return
			if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return
			const text = ev.dataTransfer?.getData('text')
			if (!text) return
			const cleaned = stripEmoji(text)
			if (cleaned !== text) {
				ev.preventDefault()
				const el = target
				const start = el.selectionStart ?? 0
				const end = el.selectionEnd ?? 0
				const newVal = el.value.slice(0, start) + cleaned + el.value.slice(end)
				el.value = newVal
				const pos = start + cleaned.length
				el.setSelectionRange(pos, pos)
			}
		},
		{ capture: true }
	)
}
